import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "../components/Header";
import Footer from "../components/Footer";

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeCategory, setActiveCategory] = useState("alla");
  const scrollRef = useRef(null);

  const categories = [
    { id: "alla", label: "Alla Projekt" },
    { id: "badrum", label: "Badrum" },
    { id: "interior", label: "Interiör" },
    { id: "exterior", label: "Exteriör" },
    { id: "kommersiellt", label: "Kommersiellt" },
  ];

  const galleryImages = [
    // Badrum
    {
      id: 1,
      category: "badrum",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/vr990qnh_20220915_154606.jpg",
      title: "LED-belyst duschnisch",
      description: "Infälld LED-belysning i duschnisch med mörk marmor",
    },
    {
      id: 2,
      category: "badrum",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/kkkovhv5_20220915_154652.jpg",
      title: "Badkar med ljusnisch",
      description: "Elegant badrum med upplyst nisch ovanför badkaret",
    },
    {
      id: 3,
      category: "badrum",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/3b3cjg70_20220915_160055.jpg",
      title: "Spegel med bakgrundsljus",
      description: "Badrumsspegel med indirekt LED-belysning",
    },
    {
      id: 4,
      category: "badrum",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/xtcfj6dn_20220905_135250.jpg",
      title: "Lyxigt marmorbadrum",
      description: "Carrara-marmor med LED-nisch och golvbelysning",
    },
    // Interiör
    {
      id: 5,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/19zia0io_20220915_164121.jpg",
      title: "Öppen planlösning",
      description: "Exklusivt vardagsrum med LED-belyst trappa och designpendlar",
    },
    {
      id: 6,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/olavwjo7_20221031_113923.jpg",
      title: "Modern hall",
      description: "Entré med infälld LED i tak och golvbelysning",
    },
    {
      id: 7,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/o8pguzdu_20230121_160003.jpg",
      title: "Vardagsrum kvällsljus",
      description: "Stämningsfullt vardagsrum med gardinskena-LED",
    },
    {
      id: 8,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/ng1icaep_20230121_160216.jpg",
      title: "LED i trätak",
      description: "Modernt vardagsrum med infälld LED i träpanel",
    },
    {
      id: 9,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/v74h7zwd_20220915_164121.jpg",
      title: "Herrgård interiör",
      description: "Klassisk villa med modern ljusdesign och LED-trappa",
    },
    // Exteriör
    {
      id: 10,
      category: "exterior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/j4rkaufh_20220913_154121.jpg",
      title: "Svart arkitektvilla",
      description: "Modern svart villa med firmabilen på plats",
    },
    {
      id: 11,
      category: "exterior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/6gnss3cm_20221031_114858.jpg",
      title: "Villa med LED-fasad",
      description: "Vit villa med LED-belysning längs takfoten",
    },
    {
      id: 12,
      category: "exterior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/t63jayj8_20221018_155801.jpg",
      title: "Modern träbyggnad",
      description: "Arkitektritat hus med LED under takfot",
    },
    // Kommersiellt
    {
      id: 13,
      category: "kommersiellt",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/6w1fwy02_20220823_175116.jpg",
      title: "Exklusiv vinbar",
      description: "Restaurang med LED i hyllor och mässingsräcke",
    },
    {
      id: 14,
      category: "kommersiellt",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/6mptego3_20230116_090818.jpg",
      title: "Restaurang belysning",
      description: "Café med dekorativa vägglampor",
    },
    {
      id: 15,
      category: "kommersiellt",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/jg0ufuui_20220922_155749.jpg",
      title: "Reception under arbete",
      description: "Kontor med infälld LED och teamet i arbete",
    },
    {
      id: 16,
      category: "kommersiellt",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/6scci8y2_20220923_175034.jpg",
      title: "Modern reception",
      description: "Exklusiv reception med LED-tak och mässingskrona",
    },
    // New images
    {
      id: 17,
      category: "badrum",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/uo16mwzc_20231227_133714.jpg",
      title: "Badrum med LED-list",
      description: "Vertikal LED-belysning vid dörr och nisch",
    },
    {
      id: 18,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/tntub5xk_20231221_114826.jpg",
      title: "Villa under arbete",
      description: "LED-lister i tak och vägg under installation",
    },
    {
      id: 19,
      category: "interior",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/99owxrij_20231221_114815.jpg",
      title: "Vardagsrum med LED-tak",
      description: "Modernt vardagsrum med infällda LED-lister och spotlights",
    },
    {
      id: 20,
      category: "badrum",
      src: "https://customer-assets.emergentagent.com/job_premium-el-design/artifacts/uq482hxi_20231221_110553.jpg",
      title: "Marmorbadrum under arbete",
      description: "LED-belysning vid tak i exklusivt marmorbadrum",
    },
  ];

  const filteredImages = activeCategory === "alla" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const currentIndex = selectedImage 
    ? filteredImages.findIndex(img => img.id === selectedImage.id) 
    : -1;

  const navigateImage = (direction) => {
    if (currentIndex === -1) return;
    const newIndex = direction === "next" 
      ? (currentIndex + 1) % filteredImages.length
      : (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setSelectedImage(filteredImages[newIndex]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;
      if (e.key === "Escape") setSelectedImage(null);
      if (e.key === "ArrowRight") navigateImage("next");
      if (e.key === "ArrowLeft") navigateImage("prev");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, currentIndex]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>Ljusdesign Inspiration | Optimal EL Stockholm</title>
        <meta name="description" content="Se våra ljusdesignprojekt i Stockholm. LED-belysning, badrumsbelysning, fasadbelysning och smart belysning för villor och fastigheter." />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container mx-auto px-6 md:px-12">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Tillbaka till startsidan
          </Link>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4"
          >
            Inspiration
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-manrope font-light text-white tracking-tight mb-6"
            data-testid="gallery-title"
          >
            Våra Projekt
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-zinc-400 text-base md:text-lg max-w-2xl"
          >
            Utforska våra senaste ljusdesignprojekt. Från exklusiva villor till 
            kommersiella lokaler – varje installation är unik.
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-8">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-3"
            data-testid="gallery-filters"
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-5 py-2.5 text-sm tracking-wide transition-all duration-300 ${
                  activeCategory === cat.id
                    ? "bg-[#D4AF37] text-black"
                    : "bg-[#0A0A0A] border border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-24 md:pb-32">
        <div className="container mx-auto px-6 md:px-12">
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="gallery-grid"
          >
            <AnimatePresence mode="popLayout">
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="group relative aspect-[4/3] overflow-hidden cursor-pointer bg-[#0A0A0A]"
                  onClick={() => setSelectedImage(image)}
                  data-testid={`gallery-image-${image.id}`}
                >
                  <img
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {/* Text */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                    <h3 className="text-white font-medium mb-1">{image.title}</h3>
                    <p className="text-zinc-400 text-sm">{image.description}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox"
          >
            {/* Close Button */}
            <button
              className="absolute top-6 right-6 text-white/70 hover:text-white p-2 z-10"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-close"
            >
              <X size={32} />
            </button>

            {/* Navigation Buttons */}
            <button
              className="absolute left-4 md:left-8 text-white/50 hover:text-white p-2 z-10"
              onClick={(e) => { e.stopPropagation(); navigateImage("prev"); }}
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={48} />
            </button>
            <button
              className="absolute right-4 md:right-8 text-white/50 hover:text-white p-2 z-10"
              onClick={(e) => { e.stopPropagation(); navigateImage("next"); }}
              data-testid="lightbox-next"
            >
              <ChevronRight size={48} />
            </button>

            {/* Image */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="max-w-5xl max-h-[80vh] mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage.src}
                alt={selectedImage.title}
                className="max-w-full max-h-[70vh] object-contain"
              />
              <div className="mt-4 text-center">
                <h3 className="text-white text-xl font-medium">{selectedImage.title}</h3>
                <p className="text-zinc-400 mt-1">{selectedImage.description}</p>
                <p className="text-zinc-600 text-sm mt-2">
                  {currentIndex + 1} / {filteredImages.length}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
};

export default GalleryPage;
